import {
  FC,
  useState,
  MouseEvent,
  KeyboardEvent,
  useEffect,
  forwardRef,
  createRef,
} from 'react';

import { v4 as uuidv4 } from 'uuid';
import { Popover } from 'react-tiny-popover';

import { PersonIcon, DownArrowIcon, UpArrowIcon } from '@assets/icons';
import CustomIcon from '@components/utils/CustomIcon';
import { validateGuestsChildrenAges } from '@components/views/landing/components/booker/common/bookerUtils';
import { validRoomChildAge } from '@components/views/hotel/listing/components/ResponsiveSearch/ResponsiveListingSearchGuest';
import { IconDelete } from '@components/ui/PriceDetailsDrawer/Item';
import useMountedRef from '@hooks/useMountedRef';

import ListingSearchRoomItem from './SearchRoomItem';
import {
  KidInterface,
  ListingSearchGuestProps,
} from './ListingSearchGuestInterface';
import {
  AddNewRoomBox,
  AddNewRoomButton,
  AddNewRoomButtonText,
  GuestContent,
  SearchGuestItem,
  SearchGuestItemLabel,
  SearchGuestItemText,
  SearchGuestItemTextWrapper,
  SearchGuestIconWrapper,
  RoomButton,
  SelectStyled,
  SelectOptionStyled,
} from './SearchGuestStyle';
import { CustomPopoverChildComponentProps } from '../SearchBarInput/SearchBarInput';

const CustomPopoverChildComponent = forwardRef<
  HTMLDivElement,
  CustomPopoverChildComponentProps
>((props, ref) => (
  <SearchGuestItem ref={ref}>{props.children}</SearchGuestItem>
));

const ListingSearchGuest: FC<ListingSearchGuestProps> = ({
  rooms,
  setHotelRooms,
  popoverStyles,
  renderParentComponent,
  isSingleLineDatePicker = false,
}) => {
  const { parentRef, isParentReady } = useMountedRef<HTMLDivElement>();

  const [roomId, setRoomId] = useState<string>();
  const [roomIdx, setRoomIdx] = useState(0);
  const [isRoomOptionsShow, setIsRoomOptionsShow] = useState(false);
  const [selectedGuest, setSelectedGuest] = useState(false);

  const toggleSelectGuest = () => setSelectedGuest((val) => !val);

  // ===================================
  // Handle toggle the select box
  // ===================================
  const handleSelect = (): void => {
    const isError = validateGuestsChildrenAges(rooms);
    if (isError) {
      handleChildError();
      focusRoomWithError();
      return;
    }
    setTimeout(() => toggleSelectGuest());
  };

  // ===================================
  // Focus the room that has an error
  // ===================================
  const focusRoomWithError = () => {
    const roomWithError = rooms.find((room) =>
      room.childrenList.find(
        (kid: KidInterface) => !validRoomChildAge(String(kid.age))
      )
    );

    if (!roomWithError) return;
  };

  // ===================================
  // Format the values before display them
  // ===================================
  const adultNumber = rooms.reduce((t, a) => t + a.adultCount, 0);
  const childrenCount = rooms.reduce((t, a) => t + a.childrenList.length, 0);
  const roomSelectBoxLabel = `${rooms.length} Oda, ${adultNumber} Yetişkin   ${
    childrenCount > 0 ? ', ' + childrenCount + ' Çocuk' : ''
  }`;

  // ===================================
  // Handle edit/remove rooms tabs
  // ===================================
  const handleEdit = (
    id:
      | string
      | MouseEvent<Element, globalThis.MouseEvent>
      | KeyboardEvent<Element>
  ): void => {
    const newRooms = rooms.filter((room) => room.key !== id);

    setHotelRooms?.(newRooms);
    setRoomIdx(0);
  };

  // ==========================================================================
  // -Custom functions to control number of adult in each room
  // -using this function we can increase the number of adult guest in each room
  // ==========================================================================
  const handleIncrementAdultNumber = (id: string): void => {
    setHotelRooms(
      rooms.map((room) =>
        room.id === id && room.adultCount < 6
          ? {
              ...room,
              adultCount: room.adultCount + 1,
            }
          : room
      )
    );
  };

  // ==========================================================================
  // -Custom functions to control number of adult in each room
  // -using this function we can decrease the number of adult guest in each room
  // ==========================================================================
  const handleDecrementAdultNumber = (id: string): void => {
    setHotelRooms(
      rooms.map((room) =>
        room.id === id ? { ...room, adultCount: room.adultCount - 1 } : room
      )
    );
  };

  // ============================================================
  // -Custom functions to control number of kids in each room
  // -using this function we can add new kid in each room
  // =============================================================
  const addNewChild = (roomId: string, newKid: KidInterface): void => {
    setHotelRooms(
      rooms.map((room) =>
        room.id === roomId && room.childrenList?.length < 3
          ? { ...room, childrenList: [...room.childrenList, newKid] }
          : room
      )
    );
  };

  // ============================================================
  // -Custom functions to control number of kids in each room
  // -using this function we can remove a kid from each room
  // =============================================================
  const removeChild = (roomId: string, kidId: string): void => {
    setHotelRooms(
      rooms.map((room) =>
        room.id === roomId
          ? {
              ...room,
              childrenList: [
                ...room.childrenList.slice(0, Number(kidId)),
                ...room.childrenList.slice(Number(kidId) + 1),
              ],
            }
          : room
      )
    );
  };

  // ============================================================
  // -Custom functions to control the age of kids in each room
  // -using this function we can change the kid's age
  // =============================================================
  const changeChildAge = (kidId: string, age: string): void => {
    setHotelRooms(
      rooms.map((room) =>
        room.childrenList.length !== 0
          ? {
              ...room,
              childrenList:
                room.childrenList &&
                room.childrenList.map((kid) =>
                  kid.id === kidId
                    ? {
                        ...kid,
                        age: age,
                        error: validRoomChildAge(age) ? false : true,
                      }
                    : kid
                ),
            }
          : room
      )
    );
  };

  // ============================================================
  // -Custom functions to control the rooms
  // -using this function we can add new room
  // =============================================================
  const addRoom = (): void => {
    setHotelRooms([
      ...rooms,
      {
        id: uuidv4(),
        adultCount: 2,
        childrenList: [],
        title: `${rooms.length + 1}. Oda`,
        closable: true,
        error: false,
        key: String(rooms[rooms.length - 1].key + 1),
        ref: createRef<HTMLDivElement>(),
      },
    ]);

    setTimeout(() => {
      setRoomIdx(rooms.length);
    }, 0);
  };

  const handleChildIncrement = (roomId: string) => {
    const newChild = {
      id: Date.now().toString(),
      age: '',
      error: false,
      inputId: `child-input-${uuidv4()}`,
      ref: createRef<HTMLDivElement>(),
    };

    addNewChild(roomId, newChild);
    setRoomId(roomId);
  };

  // ============================================================
  // -Custom functions to control the children error
  // -using this function we can find an invalid child age and update te error property
  // =============================================================
  const handleChildError = (): void => {
    setHotelRooms(
      rooms.map((room) =>
        room.childrenList.length !== 0
          ? {
              ...room,
              childrenList:
                room.childrenList &&
                room.childrenList.map((kid: KidInterface) =>
                  validRoomChildAge(String(kid.age))
                    ? { ...kid, error: false }
                    : { ...kid, error: true }
                ),
            }
          : room
      )
    );
  };

  useEffect(() => {
    const room = rooms.find((item) => item.id === roomId);

    if (room?.ref) {
      room.ref.current?.scrollTo({
        top: room.childrenList.length === 3 ? 220 : 130,
        left: 0,
        behavior: 'smooth',
      });
    }
  }, [rooms, roomId]);

  return (
    <div ref={parentRef}>
      {isParentReady ? (
        <Popover
          positions={['bottom', 'left']}
          align='start'
          padding={10}
          containerStyle={{
            zIndex: '100',
            backgroundColor: 'white',
            width: '540px',
            display: 'flex',
            justifyContent: 'center',
            margin: '0px',
            boxShadow: '0 20px 40px 0 rgba(0, 0, 0, 0.2)',
            ...popoverStyles,
          }}
          isOpen={selectedGuest}
          parentElement={parentRef?.current as HTMLElement}
          onClickOutside={() => {
            if (validateGuestsChildrenAges(rooms)) {
              handleChildError();
              focusRoomWithError();
              return;
            }

            if (isRoomOptionsShow) return;

            setSelectedGuest(false);
          }}
          content={() => {
            return (
              <GuestContent>
                <SelectStyled
                  bordered={false}
                  onChange={(v) => setRoomIdx(Number(v))}
                  value={`${roomIdx + 1}. Oda`}
                  onOpenChange={setIsRoomOptionsShow}
                  getPopupContainer={(triggerNode) => triggerNode.parentElement}
                  dropdownAlign={{
                    overflow: { adjustX: false, adjustY: false },
                  }}
                >
                  {rooms.map((room, i) => (
                    <SelectStyled.Option value={i} key={i}>
                      <SelectOptionStyled>
                        <span>{`${i + 1}. Oda`}</span>
                        {rooms.length > 1 ? (
                          <IconDelete
                            onClick={(e: MouseEvent) => {
                              handleEdit(room.key);
                              e.stopPropagation();
                            }}
                          />
                        ) : null}
                      </SelectOptionStyled>
                    </SelectStyled.Option>
                  ))}
                </SelectStyled>
                <ListingSearchRoomItem
                  key={rooms[roomIdx].id}
                  isSingleLineDatePicker={isSingleLineDatePicker}
                  adultGuestCount={rooms[roomIdx].adultCount}
                  childGuestCount={rooms[roomIdx].childrenList.length}
                  childrenList={rooms[roomIdx].childrenList}
                  guestsWrapperRef={rooms[roomIdx].ref}
                  handleAdultNumberDecrement={() =>
                    handleDecrementAdultNumber(rooms[roomIdx].id)
                  }
                  handleAdultNumberIncrement={() =>
                    handleIncrementAdultNumber(rooms[roomIdx].id)
                  }
                  handleChildNumberDecrement={() =>
                    removeChild(
                      rooms[roomIdx].id,
                      (rooms[roomIdx].childrenList.length - 1).toString()
                    )
                  }
                  handleChildNumberIncrement={() =>
                    handleChildIncrement(rooms[roomIdx].id)
                  }
                  changeChildAge={changeChildAge}
                />
                <AddNewRoomBox>
                  {!validateGuestsChildrenAges(rooms) ? (
                    <AddNewRoomButton
                      disabled={rooms.length === 6 ? true : false}
                      onClick={addRoom}
                      outline
                      data-testid='listing-search-new-room'
                    >
                      <AddNewRoomButtonText>Oda Ekle</AddNewRoomButtonText>
                    </AddNewRoomButton>
                  ) : (
                    <RoomButton
                      onClick={() => {
                        handleChildError();
                        focusRoomWithError();
                      }}
                    >
                      <AddNewRoomButtonText>Oda Ekle</AddNewRoomButtonText>
                    </RoomButton>
                  )}
                </AddNewRoomBox>
              </GuestContent>
            );
          }}
        >
          <div onClick={handleSelect} aria-hidden>
            {renderParentComponent ? (
              renderParentComponent()
            ) : (
              <CustomPopoverChildComponent>
                <CustomIcon
                  icon={PersonIcon}
                  fontSize={28}
                  paletteColor='primaryBrand'
                />
                <SearchGuestItemTextWrapper>
                  <SearchGuestItemLabel>Kişi, Oda Sayısı</SearchGuestItemLabel>
                  <SearchGuestItemText>
                    {roomSelectBoxLabel}
                  </SearchGuestItemText>
                </SearchGuestItemTextWrapper>
                {selectedGuest ? (
                  <SearchGuestIconWrapper>
                    <CustomIcon
                      icon={UpArrowIcon}
                      fontSize={16}
                      paletteColor='darkGray'
                    />
                  </SearchGuestIconWrapper>
                ) : (
                  <SearchGuestIconWrapper>
                    <CustomIcon
                      icon={DownArrowIcon}
                      fontSize={16}
                      paletteColor='darkGray'
                    />
                  </SearchGuestIconWrapper>
                )}
              </CustomPopoverChildComponent>
            )}
          </div>
        </Popover>
      ) : null}
    </div>
  );
};

export default ListingSearchGuest;
